import { EditStrategyParams, ExternalYieldSource } from "@bridgesplit/abf-sdk";
import {
    formatPercent,
    formatTokenAmount,
    percentDecimalsToUi,
    uiAmountToLamports,
    uiPercentToBps
} from "@bridgesplit/utils";
import { LendingVaultExpanded } from "@bridgesplit/abf-react";
import { COPY } from "app/constants";

import { initialSettingsForm, SettingForm } from "../type";

const SETTINGS_FORM_COMPARE_KEYS: (keyof SettingForm)[] = [
    "idleCapital",
    "originationsEnabled",
    "liquidityBuffer",
    "originationFee",
    "interestFee",
    "originationCap",
    "maxEarlyUnstakeFee"
];
const PERCENT_FIELDS = ["liquidityBuffer", "originationFee", "interestFee", "maxEarlyUnstakeFee"];
const PERCENT_RANGE_ERROR = "Must be between 0 and 100";

export function getInitialSettingsForm(vaultExpanded: LendingVaultExpanded | undefined): SettingForm {
    if (!vaultExpanded) return initialSettingsForm;
    return {
        ...initialSettingsForm,
        idleCapital: vaultExpanded.vaultStrategy?.externalYieldInfo?.externalYieldSource ?? ExternalYieldSource.None,
        originationsEnabled: vaultExpanded.vaultStrategy?.strategy.originationsEnabled,
        liquidityBuffer: percentDecimalsToUi(vaultExpanded.vaultStrategy?.strategy.liquidityBuffer),
        originationFee: percentDecimalsToUi(vaultExpanded.vaultStrategy?.strategy.originationFee),
        interestFee: percentDecimalsToUi(vaultExpanded.vaultStrategy?.strategy.interestFee),
        originationCap: vaultExpanded.vaultStrategy?.strategy.originationCap,
        maxEarlyUnstakeFee: percentDecimalsToUi(vaultExpanded.vault.maxEarlyUnstakeFee)
    };
}

export type SettingsEscrowUpdates = {
    vaultAddress: string;
    editStrategyArgs: EditStrategyParams;
    editVaultArgs?: { maxEarlyUnstakeFeeCbps: number };
};

export function calculateSettingsEscrowUpdates(
    form: SettingForm,
    vaultExpanded: LendingVaultExpanded | undefined
): SettingsEscrowUpdates | null {
    if (!vaultExpanded) return null;

    const initial = getInitialSettingsForm(vaultExpanded);
    if (!isSettingsDraftChanged(form, initial)) return null;

    const editStrategyArgs: EditStrategyParams = {
        nonce: vaultExpanded.vaultStrategy?.strategy.nonce,
        originationsEnabled:
            form.originationsEnabled ?? vaultExpanded.vaultStrategy?.strategy.originationsEnabled ?? true,
        externalYieldSource: { newExternalYieldSource: form.idleCapital ?? ExternalYieldSource.None },
        principalMint: vaultExpanded.principalMetadata.mint
    };

    if (form.originationFee !== undefined) editStrategyArgs.originationFee = uiPercentToBps(form.originationFee);
    if (form.interestFee !== undefined) editStrategyArgs.interestFee = uiPercentToBps(form.interestFee);
    if (form.originationCap !== undefined) {
        editStrategyArgs.originationCap = uiAmountToLamports(
            form.originationCap,
            vaultExpanded.principalMetadata.decimals
        );
    }
    if (form.liquidityBuffer !== undefined) editStrategyArgs.liquidityBuffer = uiPercentToBps(form.liquidityBuffer);

    const result: SettingsEscrowUpdates = {
        vaultAddress: vaultExpanded.vault.address,
        editStrategyArgs
    };

    // The form is seeded with the vault's current fee, so only send a vault edit when it actually changed
    if (form.maxEarlyUnstakeFee !== undefined && form.maxEarlyUnstakeFee !== initial.maxEarlyUnstakeFee) {
        result.editVaultArgs = { maxEarlyUnstakeFeeCbps: uiPercentToBps(form.maxEarlyUnstakeFee) };
    }

    return result;
}

/** Same as SettingsEscrowEditCta - form has no changes when settings escrow updates are null. */

export function isSettingsDraftChanged(form: SettingForm, baseline: SettingForm): boolean {
    return SETTINGS_FORM_COMPARE_KEYS.some((key) => form[key] !== baseline[key]);
}

export function isSettingsFormSame(form: SettingForm, vaultExpanded: LendingVaultExpanded | undefined): boolean {
    return calculateSettingsEscrowUpdates(form, vaultExpanded) === null;
}

export function getSettingsFormValidationErrors(form: SettingForm) {
    const errors: Partial<Record<keyof SettingForm, string>> = {};

    for (const key of PERCENT_FIELDS) {
        const value = form[key as keyof SettingForm] as number;
        if (value != null && (value < 0 || value > 100)) {
            errors[key as keyof SettingForm] = PERCENT_RANGE_ERROR;
        }
    }

    return errors;
}

export function hasSettingsFormValidationErrors(form: SettingForm): boolean {
    return Object.keys(getSettingsFormValidationErrors(form)).length > 0;
}

export type SettingsFormChangeStat = {
    caption: string;
    value: [string, string];
    hide: boolean;
};

export function getSettingsFormChangeStats(
    form: SettingForm,
    initial: SettingForm,
    vaultExpanded: LendingVaultExpanded | undefined
): SettingsFormChangeStat[] {
    if (!vaultExpanded) return [];

    return [
        {
            caption: COPY.ORIGINATION_CAP_STATUS,
            value: [
                initial.originationsEnabled ? "Accepting" : "Closed",
                form.originationsEnabled ? "Accepting" : "Closed"
            ],
            hide: form.originationsEnabled === initial.originationsEnabled
        },
        {
            caption: COPY.YIELD_SOURCE_TERM,
            value: [
                initial.idleCapital ? ExternalYieldSource[initial.idleCapital] : "None",
                form.idleCapital ? ExternalYieldSource[form.idleCapital] : "None"
            ],
            hide: form.idleCapital === initial.idleCapital
        },
        {
            caption: COPY.LIQUIDITY_BUFFER,
            value: [
                formatPercent(vaultExpanded.vaultStrategy?.strategy.liquidityBuffer),
                formatPercent(form.liquidityBuffer, { skipMultiplication: true })
            ],
            hide: form.liquidityBuffer === initial.liquidityBuffer
        },
        {
            caption: COPY.ORIGINATION_FEE,
            value: [
                formatPercent(vaultExpanded.vaultStrategy?.strategy.originationFee),
                formatPercent(form.originationFee, { skipMultiplication: true })
            ],
            hide: form.originationFee === initial.originationFee
        },
        {
            caption: COPY.INTEREST_FEE,
            value: [
                formatPercent(vaultExpanded.vaultStrategy?.strategy.interestFee),
                formatPercent(form.interestFee, { skipMultiplication: true })
            ],
            hide: form.interestFee === initial.interestFee
        },
        {
            caption: "Max early unstake fee",
            value: [
                formatPercent(vaultExpanded.vault.maxEarlyUnstakeFee),
                formatPercent(form.maxEarlyUnstakeFee, { skipMultiplication: true })
            ],
            hide: form.maxEarlyUnstakeFee === initial.maxEarlyUnstakeFee
        },
        {
            caption: COPY.MAX_ORIGINATION_SIZE,
            value: [
                formatTokenAmount(vaultExpanded.vaultStrategy?.strategy.originationCap, {
                    symbol: vaultExpanded.principalMetadata.symbol
                }),
                formatTokenAmount(form.originationCap, { symbol: vaultExpanded.principalMetadata.symbol })
            ],
            hide: form.originationCap === initial.originationCap
        }
    ];
}
